import { useCallback, useEffect, useState } from 'react'

import { ImmutableMap } from 'models/helpers'
import { MessageAttachedFile } from '../ThreadMessage'
import { ThreadMessageItem } from '../Thread.component'
import { useAuth0 } from 'utils/auth'
import { useGetAssignmentDocumentFile } from 'dataQueries/assignmentDocuments.query'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Custom hook to fetch attached files for a list of thread messages.
 *
 * @param {ThreadMessageItem[]} messages - Array of thread messages.
 * @param {string} assignmentId - The ID of the assignment.
 * 
 * @example
 * const { isLoading, messageFiles } = useFetchMessageFiles(messages, assignmentId)
 */
export const useFetchMessageFiles = (messages: ThreadMessageItem[], assignmentId: string) => {
  const { roles } = useAuth0()

  const [isFetchingMessageFiles, setIsFetchingMessageFiles] = useState(true)
  const [messageFiles, setMessageFiles] = useState<ImmutableMap<string, MessageAttachedFile[]>>(ImmutableMap([]))

  const assignmentDocumentFile = useGetAssignmentDocumentFile()
  const { userPropertyWorkspaceId } = useUserData()

  const fetchFiles = useCallback(async () => {
    setIsFetchingMessageFiles(true)

    for (const message of messages) {
      if (message.attachmentFilenames.length === 0) {
        continue
      }

      const promises = message.attachmentFilenames.map((filename) =>
        assignmentDocumentFile.mutateAsync({ assignmentId, filename, workspaceId: userPropertyWorkspaceId, isClient: roles.isClient })
      )

      try {
        const results = await Promise.all(promises)

        const updatedFiles: MessageAttachedFile[] = results.map((result) => ({
          id: result.data.name,
          fileName: result.data.originalName ?? result.data.signedUrl.filename,
          fileUrl: result.data.signedUrl.signedURL,
        }))

        setMessageFiles((prevItems) => prevItems.set(message.id, updatedFiles))
      } catch {
        // If the file fetching fails, set an empty array for the message
        setMessageFiles((prevItems) => prevItems.set(message.id, []))
      }
    }

  }, [messages, assignmentDocumentFile, assignmentId, userPropertyWorkspaceId, roles.isClient])

  useEffect(() => {
    if (messages.length > 0) {
      fetchFiles()
      setIsFetchingMessageFiles(false)
    } else {
      setIsFetchingMessageFiles(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  return { isFetchingMessageFiles, messageFiles }
}
